import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "../../utils/useTranslation";
import {
  Dimensions,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useAppTheme } from "../../hooks/useAppTheme";
import type { ThemeColors } from "../../utils/constants/colors";
import { useIsFocused } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../components/shared/Spinner";
import { mdorderHeaders, orderHeaders, smorderHeaders } from "./orderHeaders";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import {
  FAB,
  DataTable,
  ThemeProvider,
  useTheme as usePaperTheme,
} from "react-native-paper";
import { Ionicons, Feather } from "@expo/vector-icons";
import statusColorCodes from "../../utils/constants/statusColorCodes";
import { formatValueAsDataType } from "../../utils/common";

import { getOrders } from "../../redux/dispatcher/portalDispatcher";
import {
  setOrderCategoryFilter,
  setOrderPage,
  setOrderSearchQuery,
  setRequestType,
  setOrderDateFilter,
} from "../../redux/reducers/portalReducer";
import { PortalNestedDataTable } from "../../components/custom/DataTable/PortalNestedDataTable";
import { DynamicLabelBannerWithSearchBar } from "../../components/custom/Banners/DynamicLabelBannerWithSearchBar";
import CustomerOrderShipmentStatusUpdate from "./partials/CustomerOrderShipmentStatusUpdate";
import { DatePickerModal, registerTranslation } from "react-native-paper-dates";

registerTranslation("en-AU", {
  save: "Save",
  selectSingle: "Select date",
  selectMultiple: "Select dates",
  selectRange: "Select period",
  notAccordingToDateFormat: (inputFormat) =>
    `Date format must be ${inputFormat}`,
  mustBeHigherThan: (date) => `Must be later than ${date}`,
  mustBeLowerThan: (date) => `Must be earlier than ${date}`,
  mustBeBetween: (startDate, endDate) =>
    `Must be between ${startDate} - ${endDate}`,
  dateIsDisabled: "Day is not allowed",
  previous: "Previous",
  next: "Next",
  typeInDate: "Type in date",
  pickDateFromCalendar: "Pick date from calendar",
  close: "Close",
  hour: "Hour",
  minute: "Minute",
  startDate: "Start Date (DD/MM/YYYY)",
  endDate: "End Date (DD/MM/YYYY)",
});

export default function CustomerOrderScreen({ navigation }: any) {
  const isFocused = useIsFocused();
  const dispatch = useDispatch<any>();
  const { t } = useTranslation();
  const { colors, isDark } = useAppTheme();
  const styles = React.useMemo(
    () => createStyles(colors, isDark),
    [colors, isDark],
  );
  const cardStyles = React.useMemo(
    () => createCardStyles(colors, isDark),
    [colors, isDark],
  );
  const paperTheme = usePaperTheme();

  // Get portal state from Redux
  const {
    orders,
    loading,
    searching,
    totalCount,
    totalPages,
    page,
    pageSize,
    searchQuery,
    categoryFilter,
    sortBy,
    requestType,
    dateFilter,
  } = useSelector((state: any) => state.portal);

  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      orderHeaders,
    );

  // Customer Modal Props
  const [modalOpen, setModalOpen] = useState<boolean>();
  const [selectedEnquiryType, setSelectedEnquiryType] = useState<any>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const onClose = () => {
    setModalOpen(false);
  };

  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);

  const dateFiltersList = [
    "All",
    "Last 7 days",
    "Yesterday",
    "Today",
    "Bi-Weekly",
    "Next 7 days",
    "Next 14 days",
    "Next 30 days",
    "Date Range",
  ];
  const requestTypeList = ["Request Delivery", "Made To Order", "Pick up"];
  const statusList = [
    "Cancelled",
    "Pending For Approval",
    "Pending For Paperwork",
    "Approved",
    "Ready",
    "Back-Order",
    "Shipped",
  ];

  const listRef = useRef<ScrollView>(null);
  const [contentOffSet, setContentOffSet] = useState<number>(0);
  const [fabPosition] = useState<number>(320);
  const [isAtBottom, setIsAtBottom] = useState<boolean>(false);

  const handleScroll = (event: any) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    setContentOffSet(contentOffset.y);

    const isCloseToBottom =
      layoutMeasurement.height + contentOffset.y >= contentSize.height - 200;
    setIsAtBottom(isCloseToBottom);
  };

  const navigateToOrder = (order: any, action: string) => {
    if (action === "view") {
      navigation.navigate("CustomerOrderSummaryReport", { order });
    } else if (action === "update_status") {
      setModalOpen(true);
      setSelectedItem(order);
      setSelectedEnquiryType(action);
    }
  };

  // Fetch orders on mount and when filters change
  useEffect(() => {
    if (isFocused) {
      dispatch(getOrders() as any).unwrap();
    }
  }, [
    isFocused,
    dispatch,
    page,
    searchQuery,
    categoryFilter,
    sortBy,
    requestType,
    dateFilter,
  ]);

  // Update table headers based on device size
  useEffect(() => {
    setTableHeaders(
      isLargeDevice
        ? orderHeaders
        : isMediumDevice
          ? mdorderHeaders
          : smorderHeaders,
    );
  }, [isLargeDevice, isMediumDevice, isSmallDevice]);

  const screenWidth = Dimensions.get("window").width;

  const shouldUseCompactControls =
    isSmallDevice || isMobileDevice || screenWidth <= 1366;

  // Handle search
  const handleSearch = (query: string) => {
    dispatch(setOrderSearchQuery(query));
    dispatch(setOrderPage(0)); // reset page on search
  };

  const handleSearchTextChange = (text: string) => {
    dispatch(setOrderSearchQuery(text));
  };

  const handleQuickFilterChange = (selected: string[]) => {
    const requestTypes = ["Request Delivery", "Made To Order", "Pick up"];
    const dateFilters = [
      "Last 7 days",
      "Yesterday",
      "Today",
      "Bi-Weekly",
      "Next 7 days",
      "Next 14 days",
      "Next 30 days",
    ];
    const selectedRequestTypes = selected.filter((s) =>
      requestTypes.includes(s),
    );
    const selectedDateFilters = selected.filter((s) => dateFilters.includes(s));
    const selectedStatuses = selected.filter(
      (s) => !requestTypes.includes(s) && !dateFilters.includes(s),
    );

    dispatch(
      setRequestType(
        selectedRequestTypes.length === 0
          ? "all"
          : selectedRequestTypes.join(","),
      ),
    );
    dispatch(
      setOrderCategoryFilter(
        selectedStatuses.length === 0 ? "all" : selectedStatuses.join(","),
      ),
    );
    dispatch(
      setOrderDateFilter(
        selectedDateFilters.length === 0
          ? "all"
          : selectedDateFilters.join(","),
      ),
    );
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    dispatch(setOrderPage(newPage));
  };

  // Handle refresh
  const handleRefresh = () => {
    dispatch(getOrders() as any);
  };

  const [selectedDates, setSelectedDates] = useState<string[]>([
    dateFilter || "Bi-Weekly",
  ]);
  const [selectedRequestTypes, setSelectedRequestTypes] = useState<string[]>(
    requestType === "all" || !requestType ? [] : requestType.split(","),
  );
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    categoryFilter === "all" || !categoryFilter
      ? []
      : categoryFilter.split(","),
  );

  const [mobileFilterOpen, setMobileFilterOpen] = useState<
    "date" | "requestType" | "status" | null
  >(null);
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<{
    startDate: Date | undefined;
    endDate: Date | undefined;
  }>({ startDate: undefined, endDate: undefined });

  const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleDateConfirm = (params: any) => {
    setDatePickerOpen(false);
    if (params.startDate) {
      const start = params.startDate;
      const end = params.endDate || params.startDate;
      setCustomDateRange({ startDate: start, endDate: end });
      const startDateStr = formatLocalDate(start);
      const endDateStr = formatLocalDate(end);
      dispatch(setOrderDateFilter(`Custom:${startDateStr}_${endDateStr}`));
      setSelectedDates([`Custom:${startDateStr}_${endDateStr}`]);
    }
  };

  const getDisplayDate = (dateStr: string) => {
    if (!dateStr) return "All";
    if (dateStr.startsWith("Custom:")) {
      const dates = dateStr.replace("Custom:", "").split("_");
      if (dates.length === 2) {
        const [startYear, startMonth, startDay] = dates[0].split("-");
        const [endYear, endMonth, endDay] = dates[1].split("-");
        return `${startDay}/${startMonth}/${startYear} to ${endDay}/${endMonth}/${endYear}`;
      }
    }
    return dateStr;
  };

  const handleDateDismiss = () => {
    setDatePickerOpen(false);
    if (selectedDates.length === 0 || selectedDates[0].startsWith("Custom")) {
      // Do nothing if already custom or empty
    }
  };

  const columnsToNotHaveThousandSeparator: Array<string> = [
    "packingNo",
    "Hubspot ID",
    "docNumbers",
    "Customer Number",
    "orderNo",
  ];

  const MobileOrderCard = ({ order }: { order: any }) => {
    return (
      <TouchableOpacity
        style={cardStyles.card}
        onPress={() => navigateToOrder(order, "view")}
        activeOpacity={0.7}
      >
        <View style={cardStyles.cardHeader}>
          <View style={cardStyles.cardHeaderLeft}>
            <View style={cardStyles.containerIconWrap}>
              <Ionicons
                name="document-text-outline"
                size={18}
                color={isDark ? colors.primary : "#3B82F6"}
              />
            </View>
            <View style={{ flex: 1, backgroundColor: "transparent" }}>
              <Text
                style={cardStyles.containerNumber}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {order.docNumbers || order.orderNo}
              </Text>
              {!!order.organizationName && (
                <Text
                  style={cardStyles.containerSubtitle}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                >
                  {order.organizationName}
                </Text>
              )}
            </View>
          </View>
          {!!order.orderStatus && (
            <View
              style={[
                cardStyles.statusBadge,
                {
                  backgroundColor:
                    (statusColorCodes[order.orderStatus] ||
                      colors.textSecondary) + "1A",
                },
              ]}
            >
              <View
                style={[
                  cardStyles.statusDot,
                  {
                    backgroundColor:
                      statusColorCodes[order.orderStatus] ||
                      colors.textSecondary,
                  },
                ]}
              />
              <Text
                style={[
                  cardStyles.statusBadgeText,
                  {
                    color:
                      statusColorCodes[order.orderStatus] ||
                      colors.textSecondary,
                  },
                ]}
                numberOfLines={1}
              >
                {order.orderStatus}
              </Text>
            </View>
          )}
        </View>

        <View style={cardStyles.divider} />

        <View style={cardStyles.cardFooter}>
          {!!order.requestType && (
            <View style={cardStyles.metaChip}>
              <Ionicons
                name="cube-outline"
                size={12}
                color={isDark ? colors.textSecondary : "#64748B"}
              />
              <Text style={cardStyles.metaChipText}>{order.requestType}</Text>
            </View>
          )}
          {!!order.createdByEmail && (
            <View style={cardStyles.metaChip}>
              <Ionicons
                name="person-outline"
                size={12}
                color={isDark ? colors.textSecondary : "#64748B"}
              />
              <Text
                style={cardStyles.metaChipText}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {order.createdByEmail}
              </Text>
            </View>
          )}
          {!!order.createdAt && (
            <View style={cardStyles.metaChip}>
              <Ionicons
                name="calendar-outline"
                size={12}
                color={isDark ? colors.textSecondary : "#64748B"}
              />
              <Text style={cardStyles.metaChipText}>
                {formatValueAsDataType(order.createdAt, false)}
              </Text>
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      {isSmallDevice || isMobileDevice ? (
        <>
          {/* Mobile: Search bar + filter toggle in a card, grey page background behind */}
          <View style={styles.mobileFilterCard}>
            <DynamicLabelBannerWithSearchBar
              titleName={`${t("totalItems")}: ${totalCount || 0}`}
              setRefresh={handleRefresh}
              dataToExport={orders}
              fileName="orderReport"
              sheetName="Orders"
              disableTitle={false}
              noBorder
              handleSearch={handleSearch}
              onSearchTextChange={handleSearchTextChange}
              _searchText={searchQuery}
              middleSearchControls={
                <TouchableOpacity
                  style={[
                    styles.mobileFilterToggle,
                    showMobileFilters && styles.mobileFilterToggleActive,
                  ]}
                  onPress={() => setShowMobileFilters(!showMobileFilters)}
                >
                  <Feather
                    name="filter"
                    size={15}
                    color={showMobileFilters ? "#FFFFFF" : "#6B7280"}
                  />
                </TouchableOpacity>
              }
            />

            {/* Mobile: Dropdown filter buttons */}
            {showMobileFilters && (
              <View style={styles.mobileFilterWrapper}>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <TouchableOpacity
                    style={[
                      styles.mobileDropdownButton,
                      mobileFilterOpen === "date" &&
                        styles.mobileDropdownButtonActive,
                    ]}
                    onPress={() =>
                      setMobileFilterOpen(
                        mobileFilterOpen === "date" ? null : "date",
                      )
                    }
                  >
                    <Text style={styles.mobileDropdownLabel}>
                      {t("date")}:{" "}
                    </Text>
                    <Text style={styles.mobileDropdownValue}>
                      {selectedDates.length === 0 ? "All" : selectedDates[0]}
                    </Text>
                    <Text style={styles.mobileDropdownArrow}>
                      {mobileFilterOpen === "date" ? "▲" : "▼"}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[
                      styles.mobileDropdownButton,
                      mobileFilterOpen === "requestType" &&
                        styles.mobileDropdownButtonActive,
                      { marginLeft: 16 },
                    ]}
                    onPress={() =>
                      setMobileFilterOpen(
                        mobileFilterOpen === "requestType"
                          ? null
                          : "requestType",
                      )
                    }
                  >
                    <Text style={styles.mobileDropdownLabel}>
                      {t("requestType")}:{" "}
                    </Text>
                    <Text style={styles.mobileDropdownValue}>
                      {selectedRequestTypes.length === 0
                        ? "All"
                        : selectedRequestTypes.length === 1
                          ? selectedRequestTypes[0]
                          : `${selectedRequestTypes.length} ${t("selected")}`}
                    </Text>
                    <Text style={styles.mobileDropdownArrow}>
                      {mobileFilterOpen === "requestType" ? "▲" : "▼"}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[
                      styles.mobileDropdownButton,
                      mobileFilterOpen === "status" &&
                        styles.mobileDropdownButtonActive,
                      { marginLeft: 16 },
                    ]}
                    onPress={() =>
                      setMobileFilterOpen(
                        mobileFilterOpen === "status" ? null : "status",
                      )
                    }
                  >
                    <Text style={styles.mobileDropdownLabel}>
                      {t("status")}:{" "}
                    </Text>
                    <Text style={styles.mobileDropdownValue}>
                      {selectedStatuses.length === 0
                        ? "All"
                        : selectedStatuses.length === 1
                          ? selectedStatuses[0]
                          : `${selectedStatuses.length} ${t("selected")}`}
                    </Text>
                    <Text style={styles.mobileDropdownArrow}>
                      {mobileFilterOpen === "status" ? "▲" : "▼"}
                    </Text>
                  </TouchableOpacity>
                </ScrollView>

                {mobileFilterOpen === "date" && (
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={[
                      styles.mobileChipRow,
                      { marginTop: 10 },
                    ]}
                  >
                    {dateFiltersList.map((option, index) => {
                      const isActive =
                        option === "All"
                          ? selectedDates.length === 0
                          : option === "Date Range"
                            ? selectedDates.length > 0 &&
                              selectedDates[0].startsWith("Custom")
                            : selectedDates.includes(option);
                      return (
                        <TouchableOpacity
                          key={`date-${index}`}
                          style={[
                            styles.filterChip,
                            isActive && styles.filterChipActive,
                          ]}
                          onPress={() => {
                            if (option === "All") {
                              setSelectedDates([]);
                              dispatch(setOrderDateFilter("all"));
                            } else if (option === "Date Range") {
                              setDatePickerOpen(true);
                            } else {
                              const newDates = selectedDates.includes(option)
                                ? []
                                : [option];
                              setSelectedDates(newDates);
                              dispatch(
                                setOrderDateFilter(
                                  newDates.length === 0 ? "all" : newDates[0],
                                ),
                              );
                            }
                          }}
                        >
                          <Text
                            style={
                              isActive
                                ? styles.filterChipTextActive
                                : styles.filterChipText
                            }
                          >
                            {option}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                )}

                {mobileFilterOpen === "requestType" && (
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={[
                      styles.mobileChipRow,
                      { marginTop: 10 },
                    ]}
                  >
                    <TouchableOpacity
                      style={[
                        styles.filterChip,
                        selectedRequestTypes.length === 0 &&
                          styles.filterChipActive,
                      ]}
                      onPress={() => {
                        setSelectedRequestTypes([]);
                        dispatch(setRequestType("all"));
                      }}
                    >
                      <Text
                        style={
                          selectedRequestTypes.length === 0
                            ? styles.filterChipTextActive
                            : styles.filterChipText
                        }
                      >
                        All
                      </Text>
                    </TouchableOpacity>
                    {requestTypeList.map((option, index) => {
                      const isActive = selectedRequestTypes.includes(option);
                      return (
                        <TouchableOpacity
                          key={`rt-${index}`}
                          style={[
                            styles.filterChip,
                            isActive && styles.filterChipActive,
                          ]}
                          onPress={() => {
                            const newReqs = isActive
                              ? selectedRequestTypes.filter((s) => s !== option)
                              : [...selectedRequestTypes, option];
                            setSelectedRequestTypes(newReqs);
                            dispatch(
                              setRequestType(
                                newReqs.length === 0
                                  ? "all"
                                  : newReqs.join(","),
                              ),
                            );
                          }}
                        >
                          <Text
                            style={
                              isActive
                                ? styles.filterChipTextActive
                                : styles.filterChipText
                            }
                          >
                            {option}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                )}

                {mobileFilterOpen === "status" && (
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={[
                      styles.mobileChipRow,
                      { marginTop: 10 },
                    ]}
                  >
                    <TouchableOpacity
                      style={[
                        styles.filterChip,
                        selectedStatuses.length === 0 &&
                          styles.filterChipActive,
                      ]}
                      onPress={() => {
                        setSelectedStatuses([]);
                        dispatch(setOrderCategoryFilter("all"));
                      }}
                    >
                      <Text
                        style={
                          selectedStatuses.length === 0
                            ? styles.filterChipTextActive
                            : styles.filterChipText
                        }
                      >
                        All
                      </Text>
                    </TouchableOpacity>
                    {statusList.map((option, index) => {
                      const isActive = selectedStatuses.includes(option);
                      return (
                        <TouchableOpacity
                          key={`st-${index}`}
                          style={[
                            styles.filterChip,
                            isActive && styles.filterChipActive,
                          ]}
                          onPress={() => {
                            const newStatus = isActive
                              ? selectedStatuses.filter((s) => s !== option)
                              : [...selectedStatuses, option];
                            setSelectedStatuses(newStatus);
                            dispatch(
                              setOrderCategoryFilter(
                                newStatus.length === 0
                                  ? "all"
                                  : newStatus.join(","),
                              ),
                            );
                          }}
                        >
                          <Text
                            style={
                              isActive
                                ? styles.filterChipTextActive
                                : styles.filterChipText
                            }
                          >
                            {option}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                )}
              </View>
            )}
          </View>
        </>
      ) : (
        /* Desktop: Filters on left, Search on right */
        <>
          <View style={styles.quickFilterBar}>
            <View style={{ flex: 1 }}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.quickFilterChipsContainer}
              >
                <Text style={styles.quickFilterLabel}>{t("quickFilter")}</Text>
                <TouchableOpacity
                  style={[
                    styles.mobileDropdownButton,
                    mobileFilterOpen === "date" &&
                      styles.mobileDropdownButtonActive,
                  ]}
                  onPress={() =>
                    setMobileFilterOpen(
                      mobileFilterOpen === "date" ? null : "date",
                    )
                  }
                >
                  <Text style={styles.mobileDropdownLabel}>{t("date")}: </Text>
                  <Text style={styles.mobileDropdownValue}>
                    {selectedDates.length === 0
                      ? "All"
                      : getDisplayDate(selectedDates[0])}
                  </Text>
                  <Text style={styles.mobileDropdownArrow}>
                    {mobileFilterOpen === "date" ? "▲" : "▼"}
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.mobileDropdownButton,
                    mobileFilterOpen === "requestType" &&
                      styles.mobileDropdownButtonActive,
                    { marginLeft: 8 },
                  ]}
                  onPress={() =>
                    setMobileFilterOpen(
                      mobileFilterOpen === "requestType" ? null : "requestType",
                    )
                  }
                >
                  <Text style={styles.mobileDropdownLabel}>
                    {t("requestType")}:{" "}
                  </Text>
                  <Text style={styles.mobileDropdownValue}>
                    {selectedRequestTypes.length === 0
                      ? "All"
                      : selectedRequestTypes.length === 1
                        ? selectedRequestTypes[0]
                        : `${selectedRequestTypes.length} ${t("selected")}`}
                  </Text>
                  <Text style={styles.mobileDropdownArrow}>
                    {mobileFilterOpen === "requestType" ? "▲" : "▼"}
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.mobileDropdownButton,
                    mobileFilterOpen === "status" &&
                      styles.mobileDropdownButtonActive,
                    { marginLeft: 8 },
                  ]}
                  onPress={() =>
                    setMobileFilterOpen(
                      mobileFilterOpen === "status" ? null : "status",
                    )
                  }
                >
                  <Text style={styles.mobileDropdownLabel}>
                    {t("status")}:{" "}
                  </Text>
                  <Text style={styles.mobileDropdownValue}>
                    {selectedStatuses.length === 0
                      ? "All"
                      : selectedStatuses.length === 1
                        ? selectedStatuses[0]
                        : `${selectedStatuses.length} ${t("selected")}`}
                  </Text>
                  <Text style={styles.mobileDropdownArrow}>
                    {mobileFilterOpen === "status" ? "▲" : "▼"}
                  </Text>
                </TouchableOpacity>
              </ScrollView>
            </View>
            <View style={styles.quickFilterSearchSection}>
              <DynamicLabelBannerWithSearchBar
                titleName={`${t("totalItems")}: ${totalCount || 0}`}
                setRefresh={handleRefresh}
                dataToExport={orders}
                fileName="orderReport"
                sheetName="Orders"
                disableTitle={false}
                noBorder
                handleSearch={handleSearch}
                onSearchTextChange={handleSearchTextChange}
                _searchText={searchQuery}
              />
            </View>
          </View>

          {/* Desktop Expanded Chips */}
          {mobileFilterOpen === "date" && (
            <View style={styles.mobileFilterWrapper}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.mobileChipRow}
              >
                {dateFiltersList.map((option, index) => {
                  const isActive =
                    option === "All"
                      ? selectedDates.length === 0
                      : option === "Date Range"
                        ? selectedDates.length > 0 &&
                          selectedDates[0].startsWith("Custom")
                        : selectedDates.includes(option);
                  return (
                    <TouchableOpacity
                      key={`date-${index}`}
                      style={[
                        styles.filterChip,
                        isActive && styles.filterChipActive,
                      ]}
                      onPress={() => {
                        if (option === "All") {
                          setSelectedDates([]);
                          dispatch(setOrderDateFilter("all"));
                        } else if (option === "Date Range") {
                          setDatePickerOpen(true);
                        } else {
                          const newDates = selectedDates.includes(option)
                            ? []
                            : [option];
                          setSelectedDates(newDates);
                          dispatch(
                            setOrderDateFilter(
                              newDates.length === 0 ? "all" : newDates[0],
                            ),
                          );
                        }
                      }}
                    >
                      <Text
                        style={
                          isActive
                            ? styles.filterChipTextActive
                            : styles.filterChipText
                        }
                      >
                        {option}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
          )}

          {mobileFilterOpen === "requestType" && (
            <View style={styles.mobileFilterWrapper}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.mobileChipRow}
              >
                <TouchableOpacity
                  style={[
                    styles.filterChip,
                    selectedRequestTypes.length === 0 &&
                      styles.filterChipActive,
                  ]}
                  onPress={() => {
                    setSelectedRequestTypes([]);
                    dispatch(setRequestType("all"));
                  }}
                >
                  <Text
                    style={
                      selectedRequestTypes.length === 0
                        ? styles.filterChipTextActive
                        : styles.filterChipText
                    }
                  >
                    All
                  </Text>
                </TouchableOpacity>
                {requestTypeList.map((option, index) => {
                  const isActive = selectedRequestTypes.includes(option);
                  return (
                    <TouchableOpacity
                      key={`rt-${index}`}
                      style={[
                        styles.filterChip,
                        isActive && styles.filterChipActive,
                      ]}
                      onPress={() => {
                        const newReqs = isActive
                          ? selectedRequestTypes.filter((s) => s !== option)
                          : [...selectedRequestTypes, option];
                        setSelectedRequestTypes(newReqs);
                        dispatch(
                          setRequestType(
                            newReqs.length === 0 ? "all" : newReqs.join(","),
                          ),
                        );
                      }}
                    >
                      <Text
                        style={
                          isActive
                            ? styles.filterChipTextActive
                            : styles.filterChipText
                        }
                      >
                        {option}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
          )}

          {mobileFilterOpen === "status" && (
            <View style={styles.mobileFilterWrapper}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.mobileChipRow}
              >
                <TouchableOpacity
                  style={[
                    styles.filterChip,
                    selectedStatuses.length === 0 && styles.filterChipActive,
                  ]}
                  onPress={() => {
                    setSelectedStatuses([]);
                    dispatch(setOrderCategoryFilter("all"));
                  }}
                >
                  <Text
                    style={
                      selectedStatuses.length === 0
                        ? styles.filterChipTextActive
                        : styles.filterChipText
                    }
                  >
                    All
                  </Text>
                </TouchableOpacity>
                {statusList.map((option, index) => {
                  const isActive = selectedStatuses.includes(option);
                  return (
                    <TouchableOpacity
                      key={`st-${index}`}
                      style={[
                        styles.filterChip,
                        isActive && styles.filterChipActive,
                      ]}
                      onPress={() => {
                        const newStatus = isActive
                          ? selectedStatuses.filter((s) => s !== option)
                          : [...selectedStatuses, option];
                        setSelectedStatuses(newStatus);
                        dispatch(
                          setOrderCategoryFilter(
                            newStatus.length === 0
                              ? "all"
                              : newStatus.join(","),
                          ),
                        );
                      }}
                    >
                      <Text
                        style={
                          isActive
                            ? styles.filterChipTextActive
                            : styles.filterChipText
                        }
                      >
                        {option}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
          )}
        </>
      )}

      {modalOpen && selectedEnquiryType === "update_status" && (
        <CustomerOrderShipmentStatusUpdate
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      <ScrollView
        style={styles.contentContainer}
        onScroll={handleScroll}
        ref={listRef}
      >
        {loading && !searching ? (
          <View style={styles.spinnerContainer}>
            <Spinner size="small" color="#74BBFF" />
          </View>
        ) : (
          <View
            style={{ backgroundColor: isDark ? colors.background : "#eef2f6" }}
          >
            {isAtBottom && (
              <FAB
                icon="arrow-up"
                style={[
                  styles.fab,
                  {
                    top:
                      contentOffSet +
                      Dimensions.get("window").height -
                      fabPosition -
                      120,
                  },
                ]}
                isAtBottom={isAtBottom}
                onPress={() => {
                  if (listRef.current) {
                    listRef.current.scrollTo({ y: 0, animated: true });
                  }
                }}
                small
              />
            )}
            {isSmallDevice || isMobileDevice ? (
              searching ? (
                <View style={styles.spinnerContainer}>
                  <Spinner size="small" color="#74BBFF" />
                </View>
              ) : (
                <View style={cardStyles.cardList}>
                  {(orders || []).length === 0 ? (
                    <View style={cardStyles.emptyState}>
                      <Ionicons
                        name="document-text-outline"
                        size={48}
                        color="#CBD5E1"
                      />
                      <Text style={cardStyles.emptyStateText}>
                        {t("noDataAvailable")}
                      </Text>
                    </View>
                  ) : (
                    <>
                      {orders.map((order: any, index: number) => (
                        <MobileOrderCard
                          key={
                            order.documentNumber ||
                            `${order.orderNo || "order"}-${index}`
                          }
                          order={order}
                        />
                      ))}
                      <DataTable.Pagination
                        page={page - 1}
                        numberOfPages={totalPages}
                        onPageChange={(newPage) =>
                          handlePageChange(newPage + 1)
                        }
                        label={`${(page - 1) * pageSize + 1}-${Math.min(
                          page * pageSize,
                          totalCount,
                        )} of ${totalCount}`}
                        showFastPaginationControls
                      />
                    </>
                  )}
                </View>
              )
            ) : (
              <PortalNestedDataTable
                itemsPerPage={pageSize}
                isNestedDataLoaded={!loading}
                nestedHeader={tableHeaders}
                nestedRows={orders}
                rowKeyValue={"documentNumber"}
                key="rowKeyValue"
                nestedColumnsToNotHaveThousandSeparator={
                  columnsToNotHaveThousandSeparator
                }
                isDisplayTimeWithDate={false}
                handleNestedSortColumn={() => {}}
                disableHeader={false}
                operationActions={navigateToOrder}
                isDetail={false}
                setModalVisible={() => {}}
                handleRowClick={() => {}}
                setSelected={() => {}}
                // Server-side pagination props
                useServerPagination={true}
                currentPage={page}
                totalPages={totalPages}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                loading={searching}
                disableStatusBadge={true}
                allowDefaultView={false}
              />
            )}
          </View>
        )}
      </ScrollView>
      {/* @ts-ignore */}
      <ThemeProvider
        theme={{
          ...paperTheme,
          colors: { ...paperTheme.colors, primary: colors.primary },
        }}
      >
        <DatePickerModal
          locale="en-AU"
          mode="range"
          visible={datePickerOpen}
          onDismiss={handleDateDismiss}
          startDate={customDateRange.startDate}
          endDate={customDateRange.endDate}
          onConfirm={handleDateConfirm}
        />
      </ThemeProvider>
    </View>
  );
}

function createStyles(colors: ThemeColors, isDark: boolean) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: isDark ? colors.background : "#eef2f6",
    },
    quickFilterBar: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.background,
      paddingTop: 8,
      paddingBottom: 4,
      paddingHorizontal: 10,
    },
    quickFilterChipsContainer: {
      flexDirection: "row",
      alignItems: "center",
      paddingRight: 12,
    },
    quickFilterLabel: {
      fontWeight: "700",
      fontSize: 13,
      color: colors.text,
      marginRight: 10,
    },
    quickFilterSearchSection: {
      flexShrink: 0,
      minWidth: 350,
    },
    mobileFilterCard: {
      marginHorizontal: 10,
      marginTop: 10,
      marginBottom: 8,
      backgroundColor: isDark ? colors.surface : "#FFFFFF",
      borderRadius: 16,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      overflow: "hidden" as any,
      shadowColor: isDark ? colors.text : "#0F172A",
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.07,
      shadowRadius: 12,
      elevation: 4,
    },
    mobileFilterToggle: {
      width: 40,
      height: 40,
      borderRadius: 10,
      backgroundColor: colors.surfaceSubtle,
      justifyContent: "center",
      alignItems: "center",
      borderWidth: 1,
      borderColor: colors.border,
    },
    mobileFilterToggleActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    mobileFilterWrapper: {
      paddingHorizontal: 10,
      paddingVertical: 10,
    },
    mobileDropdownButton: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 6,
      paddingHorizontal: 12,
      backgroundColor: colors.surfaceSubtle,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: colors.border,
      alignSelf: "flex-start",
    },
    mobileDropdownButtonActive: {
      borderColor: colors.primary,
      backgroundColor: colors.primaryBg,
    },
    mobileDropdownLabel: {
      fontSize: 12,
      fontWeight: "600",
      color: colors.textSecondary,
    },
    mobileDropdownValue: {
      fontSize: 12,
      fontWeight: "700",
      color: colors.primary,
    },
    mobileDropdownArrow: {
      fontSize: 8,
      color: colors.textSecondary,
      marginLeft: 4,
    },
    mobileChipRow: {
      flexDirection: "row",
      paddingVertical: 2,
      paddingHorizontal: 4,
    },
    contentContainer: {
      flex: 1,
      backgroundColor: isDark ? colors.background : "#eef2f6",
    },
    spinnerContainer: {
      justifyContent: "center",
      alignItems: "center",
      marginTop: 20,
    },
    fab: {
      position: "absolute",
      margin: 16,
      right: 0,
      zIndex: 1,
    },
    filterChip: {
      paddingVertical: 6,
      paddingHorizontal: 14,
      backgroundColor: colors.surfaceSubtle,
      borderRadius: 999,
      marginRight: 12,
      marginBottom: 8,
      borderWidth: 1,
      borderColor: colors.border,
    },
    filterChipActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    filterChipText: {
      fontSize: 13,
      color: colors.textSecondary,
      fontWeight: "500" as const,
    },
    filterChipTextActive: {
      color: "#fff",
      fontSize: 13,
      fontWeight: "600" as const,
    },
  });
}

function createCardStyles(colors: ThemeColors, isDark: boolean) {
  return StyleSheet.create({
    cardList: {
      paddingHorizontal: 16,
      paddingBottom: 24,
      paddingTop: 8,
      gap: 12,
      backgroundColor: "transparent",
    },
    card: {
      backgroundColor: isDark ? colors.surface : "#FFFFFF",
      borderRadius: 16,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E8EDF5",
      shadowColor: isDark ? colors.text : "#1E293B",
      shadowOffset: { width: 0, height: 3 },
      shadowOpacity: 0.07,
      shadowRadius: 10,
      elevation: 3,
      overflow: "hidden" as any,
      marginBottom: 4,
    },
    cardHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 16,
      paddingVertical: 14,
      backgroundColor: isDark ? colors.surfaceSubtle : "#FAFBFF",
      gap: 8,
    },
    cardHeaderLeft: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      backgroundColor: "transparent",
      flex: 1,
      paddingRight: 10,
    },
    containerIconWrap: {
      width: 34,
      height: 34,
      borderRadius: 10,
      backgroundColor: isDark ? colors.primary + "33" : "#E1F0FA",
      alignItems: "center",
      justifyContent: "center",
    },
    containerNumber: {
      fontSize: 14,
      fontWeight: "700" as const,
      color: isDark ? colors.text : "#0F172A",
      letterSpacing: 0.2,
      backgroundColor: "transparent",
    },
    containerSubtitle: {
      fontSize: 12,
      color: isDark ? colors.textSecondary : "#64748B",
      marginTop: 2,
      backgroundColor: "transparent",
    },
    statusBadge: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      alignSelf: "flex-start",
      maxWidth: 160,
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 999,
    },
    statusDot: {
      width: 6,
      height: 6,
      borderRadius: 3,
    },
    statusBadgeText: {
      fontSize: 11,
      fontWeight: "600" as const,
      flexShrink: 1,
    },
    divider: {
      height: 1,
      backgroundColor: isDark ? colors.background : "#eef2f6",
      marginHorizontal: 16,
    },
    cardFooter: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap" as any,
      paddingHorizontal: 16,
      paddingVertical: 12,
      gap: 8,
    },
    metaChip: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: isDark ? colors.surface : "#F8FAFC",
      borderRadius: 6,
      paddingVertical: 4,
      paddingHorizontal: 8,
      gap: 4,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      flexShrink: 1,
      maxWidth: "100%",
    },
    metaChipText: {
      fontSize: 11,
      color: isDark ? colors.textSecondary : "#6B7280",
      fontWeight: "500" as const,
      flexShrink: 1,
    },
    emptyState: {
      alignItems: "center",
      paddingVertical: 60,
      gap: 12,
    },
    emptyStateText: {
      fontSize: 15,
      color: "#CBD5E1",
      fontWeight: "500" as const,
    },
  });
}
